if (!customElements.get('read-more-toggle')) {
  class ReadMoreToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) {
        return;
      }

      if (this.onClickHandler) {
        this.button.removeEventListener('click', this.onClickHandler);
      }
      this.onClickHandler = this.onClick.bind(this);
      this.button.addEventListener('click', this.onClickHandler);
      this.observeControlledContent();
      this.setExpanded(this.button.getAttribute('aria-expanded') === 'true');
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClickHandler);
      this.resizeObserver?.disconnect();
    }

    onClick(event) {
      event.preventDefault();
      const expanded = this.button.getAttribute('aria-expanded') === 'true';
      this.setExpanded(!expanded);
    }

    setExpanded(expanded) {
      const container = this.getContainer();
      const expandedClass = this.dataset.expandedClass || 'expanded';
      const readMoreLabel = this.button.dataset.readMoreLabel || '';
      const readLessLabel = this.button.dataset.readLessLabel || readMoreLabel;

      this.setContent(expanded);
      container?.classList.toggle(expandedClass, expanded);
      this.button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.button.textContent = expanded ? readLessLabel : readMoreLabel;
    }

    setContent(expanded) {
      const content = this.getControlledContent();
      const collapsedTemplate = this.getStateTemplate('[data-read-more-collapsed-content]');
      const fullTemplate = this.getStateTemplate('[data-read-more-full-content]');
      if (!content || !collapsedTemplate || !fullTemplate) {
        return;
      }

      const fullContent = fullTemplate.innerHTML;
      const collapsedContent = this.getCollapsedContent(content, collapsedTemplate, fullTemplate);
      content.innerHTML = expanded ? fullContent : collapsedContent;
    }

    getCollapsedContent(content, collapsedTemplate, fullTemplate) {
      const collapsedLines = this.getCollapsedLines();
      if (!collapsedLines) {
        return collapsedTemplate.innerHTML;
      }

      return this.getLineLimitedContent(content, fullTemplate.innerHTML, collapsedLines) || collapsedTemplate.innerHTML;
    }

    getCollapsedLines() {
      const collapsedLines = parseInt(this.dataset.collapsedLines, 10);

      return Number.isNaN(collapsedLines) ? 0 : collapsedLines;
    }

    getLineLimitedContent(content, fullContent, collapsedLines) {
      const wordCount = this.countWordsInHtml(fullContent);
      const contentWidth = content.getBoundingClientRect().width;
      if (!wordCount || !contentWidth) {
        return null;
      }

      const measurement = content.cloneNode(false);
      measurement.removeAttribute('id');
      measurement.setAttribute('aria-hidden', 'true');
      measurement.style.position = 'absolute';
      measurement.style.visibility = 'hidden';
      measurement.style.pointerEvents = 'none';
      measurement.style.left = '-9999px';
      measurement.style.top = '0';
      measurement.style.width = `${contentWidth}px`;
      measurement.style.height = 'auto';
      measurement.style.maxHeight = 'none';
      measurement.style.overflow = 'visible';
      content.after(measurement);

      let low = 1;
      let high = wordCount;
      let bestContent = '';
      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        measurement.innerHTML = this.truncateHtmlByWords(fullContent, mid);
        if (this.countRenderedLines(measurement) <= collapsedLines) {
          bestContent = measurement.innerHTML;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      measurement.remove();

      return bestContent;
    }

    countWordsInHtml(html) {
      const template = document.createElement('template');
      template.innerHTML = html;
      const textNodes = this.getTextNodes(template.content);

      return textNodes.reduce((wordCount, textNode) => {
        const words = textNode.nodeValue.match(/\S+\s*/g);

        return wordCount + (words ? words.length : 0);
      }, 0);
    }

    truncateHtmlByWords(html, wordLimit) {
      const template = document.createElement('template');
      template.innerHTML = html;
      const textNodes = this.getTextNodes(template.content);
      let remainingWords = wordLimit;

      textNodes.forEach((textNode) => {
        if (remainingWords <= 0) {
          textNode.nodeValue = '';
          return;
        }

        const words = textNode.nodeValue.match(/\S+\s*/g);
        if (!words) {
          return;
        }

        if (words.length > remainingWords) {
          textNode.nodeValue = words.slice(0, remainingWords).join('').trim();
          remainingWords = 0;
          return;
        }

        remainingWords -= words.length;
      });

      this.removeEmptyElements(template.content);

      const wrapper = document.createElement('div');
      wrapper.append(template.content.cloneNode(true));

      return wrapper.innerHTML.trim();
    }

    getTextNodes(root) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) {
        textNodes.push(walker.currentNode);
      }

      return textNodes;
    }

    removeEmptyElements(root) {
      Array.from(root.querySelectorAll('*')).reverse().forEach((element) => {
        if (element.textContent.trim()) {
          return;
        }

        if (element.querySelector('img, svg, video, iframe, input, button, select, textarea')) {
          return;
        }

        element.remove();
      });
    }

    countRenderedLines(element) {
      const range = document.createRange();
      range.selectNodeContents(element);
      const lineTops = [];
      Array.from(range.getClientRects()).forEach((rect) => {
        if (!rect.width || !rect.height) {
          return;
        }

        const top = Math.round(rect.top);
        if (!lineTops.includes(top)) {
          lineTops.push(top);
        }
      });
      range.detach();

      return lineTops.length;
    }

    getControlledContent() {
      const controlsId = this.button?.getAttribute('aria-controls');
      if (!controlsId) {
        return null;
      }

      return document.getElementById(controlsId);
    }

    getStateTemplate(selector) {
      const container = this.getContainer();

      return container?.querySelector(selector);
    }

    observeControlledContent() {
      const content = this.getControlledContent();
      if (!content || !window.ResizeObserver) {
        return;
      }

      this.resizeObserver?.disconnect();
      this.lastContentWidth = Math.round(content.getBoundingClientRect().width);
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = Math.round(entries[0].contentRect.width);
        if (!width || width === this.lastContentWidth) {
          return;
        }

        this.lastContentWidth = width;
        if (this.button?.getAttribute('aria-expanded') !== 'true') {
          this.setContent(false);
        }
      });
      this.resizeObserver.observe(content);
    }

    getContainer() {
      const selector = this.dataset.containerSelector;
      if (selector) {
        return this.closest(selector);
      }

      return this.parentElement;
    }
  }

  customElements.define('read-more-toggle', ReadMoreToggle);
}
